import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { FileBadge, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const KINDS: [string, string, string][] = [
  ["daily", "Daily", "Diario"],
  ["weekly", "Weekly", "Semanal"],
  ["monthly", "Monthly", "Mensual"],
  ["annual", "Annual", "Anual"],
  ["profitability", "Profitability", "Rentabilidad"],
  ["discrepancies", "Discrepancies", "Discrepancias"],
];
const LANGUAGES: [string, string][] = [["Spanish", "Español"], ["English", "English"]];

/** Renders the light markdown the report comes back as, without pulling in a parser. */
function Rendered({ text }: { text: string }) {
  const bold = (s: string) =>
    s.split("**").map((p, i) => (i % 2 ? <strong key={i} className="text-slate-100">{p}</strong> : p));
  return (
    <div className="space-y-1.5" data-testid="exec-report-body">
      {text.split("\n").map((raw, i) => {
        const line = raw.trimEnd();
        if (!line.trim()) return <div key={i} className="h-2" />;
        if (line.startsWith("## ") || line.startsWith("# "))
          return <h3 key={i} className="font-display text-base text-slate-100 pt-3">{bold(line.replace(/^#+ /, ""))}</h3>;
        if (line.startsWith("### "))
          return <h4 key={i} className="font-display text-sm text-slate-200 pt-2">{bold(line.slice(4))}</h4>;
        if (/^\s*[-*•]\s/.test(line))
          return (
            <div key={i} className="flex gap-2 text-sm text-slate-300 leading-relaxed">
              <span className="text-blue-400 select-none">•</span><span>{bold(line.replace(/^\s*[-*•]\s/, ""))}</span>
            </div>
          );
        if (/^\s*\d+\.\s/.test(line))
          return <div key={i} className="text-sm text-slate-300 leading-relaxed pl-1">{bold(line)}</div>;
        return <p key={i} className="text-sm text-slate-300 leading-relaxed">{bold(line)}</p>;
      })}
    </div>
  );
}

export default function ExecutiveReport() {
  const [kind, setKind] = useState("daily");
  const [language, setLanguage] = useState<string>(() => localStorage.getItem("tg.report.lang") || "Spanish");
  const [text, setText] = useState("");
  const [meta, setMeta] = useState<any>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const pick = (l: string) => { setLanguage(l); try { localStorage.setItem("tg.report.lang", l); } catch { /* ignore */ } };
  const es = language === "Spanish";

  const gen = useMutation({
    mutationFn: async () => (await api.post("/reports/executive", { kind, language })).data,
    onSuccess: (d) => { setText(d.text || ""); setMeta(d); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const pdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.post("/reports/executive.pdf", { kind, language }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fleetlio-${kind}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(es ? "Informe descargado" : "Report downloaded");
    } catch (e) { toast.error(errorMessage(e)); } finally { setPdfBusy(false); }
  };

  return (
    <div className="tg-card p-5 mb-5" data-testid="executive-report">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Sparkles className="h-4 w-4 text-blue-400" />
        <div className="font-display text-lg text-slate-100">
          {es ? "Informe ejecutivo" : "Executive report"}
        </div>
        <span className="text-xs text-slate-500">
          {es ? "interpretado, no una lista de cifras" : "interpreted, not a list of figures"}
        </span>
        <div className="flex rounded-md border border-[#26334d] overflow-hidden ml-auto" data-testid="exec-language">
          {LANGUAGES.map(([v, l]) => (
            <button key={v} onClick={() => pick(v)} data-testid={`exec-language-${v}`}
                    className={cn("font-mono text-[11px] px-3 py-1.5", language === v ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200")}>{l}</button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap rounded-md border border-[#26334d] overflow-hidden" data-testid="exec-kind">
          {KINDS.map(([v, en, sp]) => (
            <button key={v} onClick={() => setKind(v)} data-testid={`exec-kind-${v}`}
                    className={cn("font-mono text-[11px] px-3 py-1.5", kind === v ? "bg-blue-500/20 text-blue-300" : "text-slate-400 hover:text-slate-200")}>
              {es ? sp : en}
            </button>
          ))}
        </div>
        <Button size="sm" data-testid="exec-generate" disabled={gen.isPending} onClick={() => gen.mutate()}
                className="bg-blue-600 hover:bg-blue-500 text-white">
          <Sparkles className="h-4 w-4 mr-1" />
          {gen.isPending ? (es ? "Redactando…" : "Writing…") : (es ? "Generar" : "Generate")}
        </Button>
        <Button size="sm" variant="outline" data-testid="exec-pdf" disabled={pdfBusy}
                onClick={pdf} className="border-[#26334d] bg-transparent text-slate-200">
          <FileBadge className="h-4 w-4 mr-1" />{pdfBusy ? (es ? "Generando…" : "Generating…") : "PDF"}
        </Button>
        {meta?.period && (
          <span className="font-mono text-[10px] text-slate-500 ml-auto">
            {meta.period.from} → {meta.period.to}
          </span>
        )}
      </div>

      {gen.isPending && (
        <div className="text-sm text-slate-500 py-6 text-center">
          {es ? "Leyendo los datos de la flota e interpretándolos…" : "Reading the fleet data and interpreting it…"}
        </div>
      )}
      {!gen.isPending && !text && (
        <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-[#26334d] rounded-md">
          {es
            ? "Elige un periodo y pulsa Generar. El informe se escribe con los datos reales de la plataforma; lo que no haya, se dice."
            : "Pick a period and press Generate. The report is written from real platform data; whatever is missing is stated as missing."}
        </div>
      )}
      {!gen.isPending && text && (
        <div className="border-t border-[#26334d] pt-4">
          <Rendered text={text} />
        </div>
      )}
    </div>
  );
}
